import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  PlayIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

interface YouTubeConnection {
  id: string;
  userId: string;
  channelId: string;
  channelTitle: string;
  subscriberCount: number;
  lastSync: Date;
}

interface YouTubeVideo {
  id: string;
  title: string;
  description: string;
  thumbnail: string;
  publishedAt: string;
  viewCount: number;
  likeCount: number;
  commentCount: number;
}

const YouTubeIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [connection, setConnection] = useState<YouTubeConnection | null>(null);
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState<string | null>(null);

  useEffect(() => {
    fetchConnection();
  }, []);

  const fetchConnection = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/platform-integrations/youtube/status');
      setConnection(response.data.connection);
      
      if (response.data.connection) {
        fetchVideos();
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error fetching YouTube connection:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVideos = async () => {
    try {
      const response = await axios.get('/api/platform-integrations/youtube/videos?maxResults=10');
      setVideos(response.data.videos);
    } catch (error) {
      console.error('Error fetching videos:', error);
    }
  };

  const connectYouTube = async () => {
    try {
      const response = await axios.get('/api/platform-integrations/youtube/auth-url');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting YouTube:', error);
    }
  };

  const disconnectYouTube = async () => {
    if (!confirm('Are you sure you want to disconnect YouTube?')) return;

    try {
      await axios.delete('/api/platform-integrations/youtube/disconnect');
      setConnection(null);
      setVideos([]);
    } catch (error) {
      console.error('Error disconnecting YouTube:', error);
    }
  };

  const syncSubscribers = async () => {
    try {
      setSyncing(true);
      await axios.post('/api/platform-integrations/youtube/sync-subscribers');
      await fetchConnection();
      alert('Subscriber count synced!');
    } catch (error) {
      console.error('Error syncing subscribers:', error);
      alert('Failed to sync subscribers');
    } finally {
      setSyncing(false);
    }
  };

  const importVideo = async (videoId: string) => {
    try {
      setImporting(videoId);
      await axios.post('/api/platform-integrations/youtube/import', { videoId });
      alert('Video imported as post!');
    } catch (error) {
      console.error('Error importing video:', error);
      alert('Failed to import video');
    } finally {
      setImporting(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/integrations')}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Integrations
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <span className="text-5xl">📺</span>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">YouTube Integration</h1>
          <p className="text-gray-600 mt-1">
            Import videos and sync subscriber count from your YouTube channel
          </p>
        </div>
      </div>

      {!connection ? (
        /* Not Connected State */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Connect Your YouTube Channel</h2>
          <p className="text-gray-600 mb-6">
            Grant access to your YouTube channel to import videos and sync subscriber counts.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Import Videos as Posts</h3>
                <p className="text-sm text-gray-600">
                  Convert YouTube videos into Patreon posts with metadata
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Sync Subscriber Count</h3>
                <p className="text-sm text-gray-600">
                  Automatically update your YouTube subscriber count
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Channel Stats</h3>
                <p className="text-sm text-gray-600">
                  Display your channel information and statistics
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={connectYouTube}
            className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
          >
            Connect YouTube Channel
          </button>
        </div>
      ) : (
        /* Connected State */
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Channel Connected</h2>
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Active</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500">Channel Name</p>
                <p className="font-medium text-gray-900">{connection.channelTitle}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Subscribers</p>
                <p className="font-medium text-gray-900">
                  {connection.subscriberCount.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Channel ID</p>
                <p className="font-mono text-sm text-gray-600">{connection.channelId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Sync</p>
                <p className="text-sm text-gray-600">
                  {new Date(connection.lastSync).toLocaleString()}
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={syncSubscribers}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Subscribers'}
              </button>
              <button
                onClick={disconnectYouTube}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Recent Videos */}
          {videos.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Videos</h2>
              <div className="grid grid-cols-1 gap-4">
                {videos.map((video) => (
                  <div
                    key={video.id}
                    className="flex gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="relative flex-shrink-0">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-40 h-24 object-cover rounded"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <PlayIcon className="h-12 w-12 text-white opacity-80" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 truncate">{video.title}</h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                        <span>{video.viewCount.toLocaleString()} views</span>
                        <span>{video.likeCount.toLocaleString()} likes</span>
                        <span>{new Date(video.publishedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => importVideo(video.id)}
                      disabled={importing === video.id}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 self-center"
                    >
                      <VideoCameraIcon className="h-4 w-4" />
                      {importing === video.id ? 'Importing...' : 'Import'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default YouTubeIntegrationPage;
